import { ReactElement, useState } from 'react';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';

import { Modal, ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

type ConfirmModalProps = ModalProps<{
  data: { id: string | number };
  confirm: (id: string | number) => boolean;
  headerText: string;
  mainText: string;
  confirmText: string;
  buttonIcon: ReactElement<IconProps>;
  negative: boolean;
  hideCheckbox?: boolean;
  dangerText?: string;
}>;

export const ConfirmModal = (props: ConfirmModalProps) => {
  const {
    data,
    confirm,
    headerText,
    mainText,
    confirmText,
    buttonIcon,
    negative,
    hideCheckbox = false,
    dangerText,
    closeModal,
  } = props;
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { t } = useStore();
  async function submit() {
    setSubmitting(true);
    const success = await confirm(data.id);
    setSubmitting(false);
    if (success) {
      closeModal();
    }
  }

  return (
    <Modal>
      <Modal.Header text={headerText} />
      <div>
        <Text text={mainText} />
        {dangerText && <Text color="red100" text={dangerText} />}
      </div>

      {negative && !hideCheckbox && (
        <Stack space={4}>
          <Separator />
          <FormLabel text={t('modals.confirm.please')} />
          <Checkbox
            label={t('modals.confirm.understanding')}
            value={confirmed}
            onChange={() => setConfirmed(!confirmed)}
          />
        </Stack>
      )}

      <Modal.Actions>
        <Button
          variant="secondary"
          onClick={closeModal}
          isDisabled={submitting}
          text={t('modals.confirm.button.cancel')}
        />
        <Button
          variant={negative ? 'primary-red' : 'primary'}
          onClick={submit}
          isDisabled={submitting || (negative && !confirmed && !hideCheckbox)}
          text={confirmText}
          iconLeft={buttonIcon}
        />
      </Modal.Actions>
    </Modal>
  );
};
